import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface SessionTimerProps {
  maxDurationSeconds: number;
  startTime: Date | string | number | null;
  isActive: boolean;
  onTimeWarning?: () => void;
  onTimeExpired?: () => void;
  showWarningAt?: number; // seconds remaining to show warning (default 300 = 5 mins)
  className?: string;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const SessionTimer: React.FC<SessionTimerProps> = ({
  maxDurationSeconds,
  startTime,
  isActive,
  onTimeWarning,
  onTimeExpired,
  showWarningAt = 300,
  className
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(maxDurationSeconds);
  const hasShownWarningRef = useRef(false);
  const hasExpiredRef = useRef(false);

  const startTimestamp = useMemo(() => {
    if (!startTime) return null;
    const timestamp = startTime instanceof Date
      ? startTime.getTime()
      : typeof startTime === 'number'
        ? startTime
        : new Date(startTime).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }, [startTime]);

  useEffect(() => {
    if (!isActive || !startTimestamp) {
      setRemainingSeconds(maxDurationSeconds);
      hasShownWarningRef.current = false;
      hasExpiredRef.current = false;
      return;
    }

    const updateRemaining = () => {
      const elapsed = (Date.now() - startTimestamp) / 1000;
      const remaining = Math.max(0, maxDurationSeconds - elapsed);
      setRemainingSeconds(remaining);

      if (remaining <= showWarningAt && remaining > showWarningAt - 5 && !hasShownWarningRef.current) {
        hasShownWarningRef.current = true;
        onTimeWarning?.();
        toast.warning(`${Math.floor(remaining / 60)} minutes remaining`, {
          description: 'Your session will end soon. Please wrap up your conversation.',
          duration: 5000,
        });
      }

      if (remaining <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onTimeExpired?.();
        toast.error('Session time has ended', {
          description: 'The session has reached its maximum duration.',
          duration: 10000,
        });
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [startTimestamp, maxDurationSeconds, isActive, showWarningAt, onTimeWarning, onTimeExpired]);

  // Don't render if not active
  if (!isActive) return null;

  const isNormal = remainingSeconds > showWarningAt;
  const isWarning = remainingSeconds <= showWarningAt && remainingSeconds > 60;
  const isCritical = remainingSeconds <= 60;

  return (
    <div 
      className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300",
        "backdrop-blur-sm",
        isNormal && "bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400",
        isWarning && "bg-yellow-500/10 border-yellow-500/30 text-yellow-600 dark:text-yellow-400",
        isCritical && "bg-destructive/10 border-destructive/30 text-destructive animate-pulse",
        className
      )}
    >
      {isCritical ? (
        <AlertTriangle className="h-5 w-5" />
      ) : (
        <Clock className="h-5 w-5" />
      )}
      <span className="font-mono text-base font-semibold">
        {formatTime(remainingSeconds)}
      </span>
      {isCritical && (
        <span className="text-sm">remaining</span>
      )}
    </div>
  );
};

export default SessionTimer;
